import { useShow, useNavigation } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Card,
  Descriptions,
  Tag,
  Avatar,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Table,
  Popconfirm,
  Modal,
  Form,
  Input,
  Steps,
  message,
} from 'antd';
import {
  UserOutlined,
  StopOutlined,
  CheckOutlined,
  EditOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  BellOutlined,
  RocketOutlined,
  StarFilled,
  PhoneOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CallNotesModal } from '../../components/CallNotesModal';
import { PushNotificationModal } from '../../components/PushNotificationModal';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';

interface Passenger {
  id: number;
  fullName: string | null;
  phone: string;
  currentRole: string;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED';
  photoUrl: string | null;
  rating: number;
  totalTrips: number;
  createdAt: string;
  blockedAt: string | null;
  blockedReason: string | null;
  homeRegion?: { id: number; nameUz: string; nameRu: string } | null;
  fcmToken?: string | null;
}

interface TripRecord {
  id: number;
  tripType: string;
  status: string;
  pickupAddress: string;
  destinationAddress: string;
  pickupRegion?: { nameUz: string } | null;
  destinationRegion?: { nameUz: string } | null;
  pickupDistrict?: { nameUz: string } | null;
  destinationDistrict?: { nameUz: string } | null;
  finalPrice: number | null;
  priceEstimate: number;
  createdAt: string;
  driver?: { id: number; fullName: string | null; phone: string } | null;
  tariff?: string | null;
  paymentMethod?: string | null;
  passengerCount?: number | null;
  distanceKm?: number | string | null;
  durationSeconds?: number | null;
  pricingMode?: string | null;
  hasLuggage?: boolean | null;
  comment?: string | null;
  ratings?: Array<{ rating: number; raterId: number; ratedId: number; comment: string | null }>;
}

const tariffLabels: Record<string, string> = {
  STANDARD: 'Standart',
  COMFORT: 'Komfort',
  BUSINESS: 'Biznes',
  SEVEN_PLUS: '7+',
};

const tariffColors: Record<string, string> = {
  STANDARD: 'default',
  COMFORT: 'blue',
  BUSINESS: 'gold',
  SEVEN_PLUS: 'purple',
};

const paymentLabels: Record<string, string> = {
  CASH: 'Naqd',
  UZCARD: 'UzCard',
  HUMO: 'Humo',
  CARD: 'Karta',
};

const pricingModeLabels: Record<string, string> = {
  NEGOTIATION: 'Kelishuv',
  AUTO_FIND: 'Avtomatik',
  FIXED: 'Belgilangan',
};

const statusLabels: Record<string, string> = {
  ONLINE: 'Faol',
  OFFLINE: 'Nofaol',
  BLOCKED: 'Bloklangan',
};

const statusColors: Record<string, string> = {
  ONLINE: 'green',
  OFFLINE: 'default',
  BLOCKED: 'red',
};

const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: 'Shahar ichida',
  INTERREGION_TAXI: 'Shaharlararo',
  SHAHARLAR_ARO: 'Viloyat ichida',
  POSTAGE: 'Pochta',
  CARGO: 'Yuk',
  YOL_YOLAKAY: 'Savdolashish',
};

const tripTypeColors: Record<string, string> = {
  INTERCITY_TAXI: 'blue',
  INTERREGION_TAXI: 'purple',
  SHAHARLAR_ARO: 'magenta',
  POSTAGE: 'orange',
  CARGO: 'cyan',
  YOL_YOLAKAY: 'green',
};

const tripStatusLabels: Record<string, string> = {
  REQUESTED: 'So\'ralgan',
  ACCEPTED: 'Qabul qilingan',
  DRIVER_ARRIVED: 'Haydovchi yetib keldi',
  PICKED_UP: 'Olib ketildi',
  ON_THE_WAY: 'Yo\'lda',
  IN_PROGRESS: 'Jarayonda',
  COMPLETED: 'Yakunlangan',
  CANCELLED: 'Bekor qilingan',
  INTERRUPTED: 'To\'xtatilgan',
  NO_SHOW: 'Kelmadi',
};

const tripStatusColors: Record<string, string> = {
  REQUESTED: 'orange',
  ACCEPTED: 'blue',
  DRIVER_ARRIVED: 'cyan',
  PICKED_UP: 'geekblue',
  ON_THE_WAY: 'processing',
  IN_PROGRESS: 'processing',
  COMPLETED: 'green',
  CANCELLED: 'red',
  INTERRUPTED: 'volcano',
  NO_SHOW: 'magenta',
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
};

export const PassengerShow = () => {
  const { id } = useParams();
  const { list } = useNavigation();
  const { can } = usePermission();
  const { queryResult } = useShow<Passenger>({
    resource: 'admin/users',
    id,
  });
  const { data, isLoading, refetch } = queryResult;
  const passenger = data?.data;

  const [trips, setTrips] = useState<TripRecord[]>([]);
  const [tripsLoading, setTripsLoading] = useState(false);

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [callNotesOpen, setCallNotesOpen] = useState(false);

  // Push-notification modal state
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushSending, setPushSending] = useState(false);

  const sendPushToPassenger = async () => {
    if (!passenger?.id) return;
    if (!pushTitle.trim() || !pushBody.trim()) {
      message.warning('Введите заголовок и текст уведомления');
      return;
    }
    setPushSending(true);
    try {
      const res = await apiClient.post('/admin/notifications/send', {
        title: pushTitle.trim(),
        body: pushBody.trim(),
        targetType: 'USER',
        targetId: passenger.id,
      });
      const delivered = res.data?.delivered ?? 0;
      const failed = res.data?.failed ?? 0;
      if (delivered > 0) {
        message.success(`✅ Уведомление доставлено`);
      } else if (failed > 0) {
        message.error(
          'FCM не смог доставить (возможно, у пассажира нет push-токена)',
        );
      } else {
        message.info('Отправлено, но FCM не подтвердил доставку');
      }
      setPushModalOpen(false);
      setPushTitle('');
      setPushBody('');
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Не удалось отправить уведомление',
      );
    } finally {
      setPushSending(false);
    }
  };
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();

  const loadTrips = useCallback(async () => {
    if (!passenger?.id) return;
    setTripsLoading(true);
    try {
      const res = await apiClient.get(`/admin/trips`, {
        params: { clientId: passenger.id, limit: 20 },
      });
      setTrips(res.data?.data || res.data || []);
    } catch {
      setTrips([]);
    } finally {
      setTripsLoading(false);
    }
  }, [passenger?.id]);

  useEffect(() => {
    if (passenger?.id) loadTrips();
  }, [passenger?.id, loadTrips]);

  const handleBlock = async () => {
    if (!blockReason.trim()) {
      message.error('Sabab kiritilishi shart');
      return;
    }
    setBlocking(true);
    try {
      await apiClient.post(`/admin/users/${passenger?.id}/block`, {
        reason: blockReason,
      });
      message.success('Foydalanuvchi bloklandi');
      setBlockModalOpen(false);
      setBlockReason('');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async () => {
    try {
      await apiClient.post(`/admin/users/${passenger?.id}/unblock`);
      message.success('Foydalanuvchi blokdan chiqarildi');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleEdit = async (values: { fullName: string }) => {
    try {
      await apiClient.put(`/admin/users/${passenger?.id}`, values);
      message.success('Foydalanuvchi tahrirlandi');
      setEditModalOpen(false);
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const tripColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Turi',
      dataIndex: 'tripType',
      key: 'tripType',
      width: 110,
      render: (type: string) => (
        <Tag color={tripTypeColors[type] || 'default'} style={{ marginRight: 0 }}>
          {tripTypeLabels[type] || type}
        </Tag>
      ),
    },
    {
      title: 'Marshrut',
      key: 'route',
      render: (_: unknown, record: TripRecord) => {
        const formatLocation = (
          region?: { nameUz: string } | null,
          district?: { nameUz: string } | null,
          fallback?: string,
        ) => {
          const parts = [region?.nameUz, district?.nameUz].filter(Boolean);
          return parts.length ? parts.join(', ') : (fallback || '-');
        };
        const pickup = formatLocation(record.pickupRegion, record.pickupDistrict, record.pickupAddress);
        const dest = formatLocation(record.destinationRegion, record.destinationDistrict, record.destinationAddress);
        return (
          <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            <div><EnvironmentOutlined style={{ color: '#52c41a' }} /> {pickup}</div>
            <div><EnvironmentOutlined style={{ color: '#f5222d' }} /> {dest}</div>
          </div>
        );
      },
    },
    {
      title: 'Haydovchi',
      key: 'driver',
      width: 170,
      render: (_: unknown, record: TripRecord) => {
        if (!record.driver) return <span style={{ color: '#BDC3C7' }}>-</span>;
        return (
          <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            <div>{record.driver.fullName || 'Ismi yo\'q'}</div>
            <div style={{ color: '#7F8C8D' }}>{record.driver.phone}</div>
          </div>
        );
      },
    },
    {
      title: 'Haydovchi bahosi',
      key: 'driverRating',
      width: 150,
      render: (_: unknown, record: TripRecord) => {
        if (!record.driver || !record.ratings?.length) {
          return <span style={{ color: '#BDC3C7' }}>-</span>;
        }
        const driverRating = record.ratings.find(
          (r) => r.raterId === record.driver!.id && r.ratedId === passenger?.id
        );
        if (!driverRating) return <span style={{ color: '#BDC3C7' }}>-</span>;
        return (
          <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            <div>
              <StarFilled style={{ color: '#FADB14', marginRight: 4 }} />
              <span style={{ fontWeight: 600 }}>{driverRating.rating}</span>
              <span style={{ color: '#7F8C8D' }}> / 5</span>
            </div>
            {driverRating.comment && (
              <div
                style={{ color: '#7F8C8D', maxWidth: 140, whiteSpace: 'normal' }}
                title={driverRating.comment}
              >
                {driverRating.comment.length > 40
                  ? driverRating.comment.slice(0, 40) + '…'
                  : driverRating.comment}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Tarif',
      dataIndex: 'tariff',
      key: 'tariff',
      width: 90,
      render: (tariff: string | null | undefined) => {
        if (!tariff) return <span style={{ color: '#BDC3C7' }}>-</span>;
        return (
          <Tag color={tariffColors[tariff] || 'default'} style={{ marginRight: 0 }}>
            {tariffLabels[tariff] || tariff}
          </Tag>
        );
      },
    },
    {
      title: "Yo'lovchilar",
      dataIndex: 'passengerCount',
      key: 'passengerCount',
      width: 90,
      align: 'center' as const,
      render: (count: number | null | undefined) =>
        count != null ? <span><UserOutlined /> {count}</span> : <span style={{ color: '#BDC3C7' }}>-</span>,
    },
    {
      title: 'Masofa',
      dataIndex: 'distanceKm',
      key: 'distanceKm',
      width: 90,
      render: (d: number | string | null | undefined, record: TripRecord) => {
        const km = d != null ? Number(d) : null;
        const mins = record.durationSeconds ? Math.round(record.durationSeconds / 60) : null;
        if (km == null && mins == null) return <span style={{ color: '#BDC3C7' }}>-</span>;
        return (
          <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {km != null && <div>{km.toFixed(1)} km</div>}
            {mins != null && <div style={{ color: '#7F8C8D' }}>{mins} min</div>}
          </div>
        );
      },
    },
    {
      title: "To'lov",
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 90,
      render: (pm: string | null | undefined) =>
        pm ? <Tag style={{ marginRight: 0 }}>{paymentLabels[pm] || pm}</Tag> : <span style={{ color: '#BDC3C7' }}>-</span>,
    },
    {
      title: 'Narx',
      key: 'price',
      width: 110,
      render: (_: unknown, record: TripRecord) => {
        const finalP = record.finalPrice != null && record.finalPrice > 0 ? record.finalPrice : record.priceEstimate;
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            <div>{formatCurrency(finalP)}</div>
            {record.pricingMode && (
              <div style={{ color: '#7F8C8D', fontSize: 11 }}>
                {pricingModeLabels[record.pricingMode] || record.pricingMode}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => (
        <Tag color={tripStatusColors[status] || 'default'} style={{ marginRight: 0 }}>
          {tripStatusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: 'Sana',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      render: (date: string) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const mins = String(d.getMinutes()).padStart(2, '0');
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            <div>{day}.{month}.{year}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{hours}:{mins}</div>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Show
        isLoading={isLoading}
        title="Yo'lovchi"
        headerButtons={
          <Space>
            <Tooltip title="Заметки звонков (журнал операторов)">
              <Button
                icon={<PhoneOutlined style={{ color: '#10B981' }} />}
                onClick={() => setCallNotesOpen(true)}
                disabled={!passenger?.id}
              >
                Звонки
              </Button>
            </Tooltip>
            <Tooltip
              title={
                passenger?.fcmToken
                  ? 'Отправить push-уведомление пассажиру'
                  : 'У пассажира нет FCM-токена — push не дойдёт'
              }
            >
              <Button
                icon={<BellOutlined />}
                onClick={() => setPushModalOpen(true)}
                disabled={!passenger?.id}
              >
                Уведомление
              </Button>
            </Tooltip>
            {can('users', 'update') && (
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  editForm.setFieldsValue({ fullName: passenger?.fullName || '' });
                  setEditModalOpen(true);
                }}
              >
                Tahrirlash
              </Button>
            )}
            {can('users', 'block') && (
              passenger?.status === 'BLOCKED' ? (
                <Popconfirm
                  title="Blokdan chiqarishni tasdiqlaysizmi?"
                  onConfirm={handleUnblock}
                >
                  <Button type="primary" icon={<CheckOutlined />}>
                    Blokdan chiqarish
                  </Button>
                </Popconfirm>
              ) : (
                <Button danger icon={<StopOutlined />} onClick={() => setBlockModalOpen(true)}>
                  Bloklash
                </Button>
              )
            )}
          </Space>
        }
      >
        <Row gutter={[24, 24]}>
          {/* Profile Card */}
          <Col xs={24} lg={8}>
            <Card>
              <div className="text-center">
                <Avatar
                  size={100}
                  src={passenger?.photoUrl}
                  icon={<UserOutlined />}
                  style={{ marginBottom: 16 }}
                />
                <h3>{passenger?.fullName || 'Ismi yo\'q'}</h3>
                <p style={{ color: '#7F8C8D' }}>{passenger?.phone}</p>
                <Tag color={statusColors[passenger?.status || ''] || 'default'}>
                  {statusLabels[passenger?.status || ''] || passenger?.status}
                </Tag>
              </div>
            </Card>

            {/* Stats */}
            <Card title="Statistika" style={{ marginTop: 24 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Reyting"
                    value={passenger?.rating != null ? Number(passenger.rating).toFixed(1) : '-'}
                  />
                </Col>
                <Col span={12}>
                  <Statistic title="Sayohatlar" value={passenger?.totalTrips || 0} />
                </Col>
              </Row>
            </Card>

            {/* Journey Stages */}
            <Card title="Ilovadagi bosqichlar" style={{ marginTop: 24 }}>
              <Steps
                direction="vertical"
                size="small"
                current={(() => {
                  const stages = [
                    !!passenger,
                    !!passenger?.fullName,
                    !!passenger?.homeRegion,
                    !!passenger?.fcmToken,
                    (passenger?.totalTrips || 0) > 0,
                  ];
                  const idx = stages.findIndex(s => !s);
                  return idx === -1 ? stages.length : idx;
                })()}
                items={[
                  { title: 'Ro\'yxatdan o\'tish', icon: <IdcardOutlined />, status: passenger ? 'finish' : 'wait', description: passenger ? 'Bajarildi' : 'Kutilmoqda' },
                  { title: 'Ism kiritildi', icon: <UserOutlined />, status: passenger?.fullName ? 'finish' : 'wait', description: passenger?.fullName ? 'Bajarildi' : 'Kutilmoqda' },
                  { title: 'Viloyat tanlandi', icon: <EnvironmentOutlined />, status: passenger?.homeRegion ? 'finish' : 'wait', description: passenger?.homeRegion ? 'Bajarildi' : 'Kutilmoqda' },
                  { title: 'Push-bildirishnomalar', icon: <BellOutlined />, status: passenger?.fcmToken ? 'finish' : 'wait', description: passenger?.fcmToken ? 'Bajarildi' : 'Kutilmoqda' },
                  { title: 'Birinchi safar', icon: <RocketOutlined />, status: (passenger?.totalTrips || 0) > 0 ? 'finish' : 'wait', description: (passenger?.totalTrips || 0) > 0 ? 'Bajarildi' : 'Kutilmoqda' },
                ]}
              />
            </Card>
          </Col>

          {/* Details + Trip History */}
          <Col xs={24} lg={16}>
            <Card title="Ma'lumotlar" style={{ marginBottom: 24 }}>
              <Descriptions column={2}>
                <Descriptions.Item label="ID">{passenger?.id}</Descriptions.Item>
                <Descriptions.Item label="Telefon">{passenger?.phone}</Descriptions.Item>
                <Descriptions.Item label="To'liq ism">
                  {passenger?.fullName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Rol">Mijoz</Descriptions.Item>
                <Descriptions.Item label="Viloyat">
                  {passenger?.homeRegion?.nameUz || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Ro'yxatdan o'tgan">
                  {passenger?.createdAt
                    ? new Date(passenger.createdAt).toLocaleDateString('uz-UZ')
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Holat">
                  <Tag color={statusColors[passenger?.status || ''] || 'default'}>
                    {statusLabels[passenger?.status || ''] || passenger?.status}
                  </Tag>
                </Descriptions.Item>
                {passenger?.blockedAt && (
                  <>
                    <Descriptions.Item label="Bloklangan sana">
                      {new Date(passenger.blockedAt).toLocaleDateString('uz-UZ')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Bloklash sababi">
                      {passenger.blockedReason || '-'}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>

          </Col>
        </Row>

        <Card title="Sayohat tarixi" loading={tripsLoading} style={{ marginTop: 24 }}>
          <Table
            dataSource={trips}
            columns={tripColumns}
            rowKey="id"
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 10, size: 'small' }}
          />
        </Card>
      </Show>

      {/* Block Modal */}
      <Modal
        title="Foydalanuvchini bloklash"
        open={blockModalOpen}
        onCancel={() => {
          setBlockModalOpen(false);
          setBlockReason('');
        }}
        onOk={handleBlock}
        okText="Bloklash"
        cancelText="Bekor qilish"
        okButtonProps={{ danger: true, loading: blocking }}
      >
        <p style={{ marginBottom: 16 }}>
          Foydalanuvchini bloklash sababini kiriting:
        </p>
        <Input.TextArea
          value={blockReason}
          onChange={(e) => setBlockReason(e.target.value)}
          placeholder="Bloklash sababi..."
          rows={4}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Foydalanuvchini tahrirlash"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={() => editForm.submit()}
        okText="Saqlash"
        cancelText="Bekor qilish"
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            label="To'liq ism"
            name="fullName"
            rules={[{ required: true, message: 'Ismni kiriting' }]}
          >
            <Input placeholder="Ismni kiriting" />
          </Form.Item>
        </Form>
      </Modal>

      <CallNotesModal
        open={callNotesOpen}
        userId={passenger?.id ?? null}
        userName={passenger?.fullName ?? undefined}
        userPhone={passenger?.phone}
        currentColor={(passenger as any)?.callIconColor ?? null}
        onClose={() => setCallNotesOpen(false)}
        onChanged={() => refetch()}
      />

      <PushNotificationModal
        open={pushModalOpen}
        userId={passenger?.id ?? null}
        userName={passenger?.fullName ?? undefined}
        userPhone={passenger?.phone}
        hasFcmToken={!!passenger?.fcmToken}
        onClose={() => setPushModalOpen(false)}
      />
    </>
  );
};
